// src/components/modals/AvatarCropModal.tsx

import React, { useRef, useState } from 'react';
import {
  Modal,
  Stack,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  IModalStyles,
} from '@fluentui/react';
import Cropper, { ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';

interface AvatarCropModalProps {
  isOpen: boolean;
  src: string | null;
  fileError: string | null;
  onClose: () => void;
  onSave: (croppedImageUrl: string) => void;
}

export const AvatarCropModal: React.FC<AvatarCropModalProps> = ({
  isOpen,
  src,
  fileError,
  onClose,
  onSave,
}) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const [isCropping, setIsCropping] = useState(false);

  const handleSave = () => {
    const cropper = cropperRef.current?.cropper;

    if (!cropper || !src || fileError) {
      return;
    }

    setIsCropping(true);

    try {
      const croppedCanvas = cropper.getCroppedCanvas({
        width: 500,
        height: 500,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      if (!croppedCanvas) {
        throw new Error(
          'Не удалось получить обрезанное изображение'
        );
      }

      const croppedImage = croppedCanvas.toDataURL(
        'image/jpeg',
        0.95
      );

      onSave(croppedImage);
      onClose();
    } catch (error) {
      console.error(
        'Ошибка при обрезке аватара:',
        error
      );
    } finally {
      setIsCropping(false);
    }
  };

  const modalStyles: Partial<IModalStyles> = {
    main: {
      width: 500,
      maxWidth: '90vw',
      maxHeight: '90vh',
      padding: 30,
      overflow: 'auto',

      selectors: {
        '@media (min-width: 480px)': {
          width: 500,
          maxWidth: 500,
        },
      },
    },
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onClose}
      styles={modalStyles}
      titleAriaId="edit-avatar-modal"
      isBlocking={true}
      allowTouchBodyScroll={false}
    >
      <Stack tokens={{ childrenGap: 15 }}>
        <h3>Редактировать аватар</h3>

        {fileError && (
          <MessageBar
            messageBarType={MessageBarType.error}
          >
            {fileError}
          </MessageBar>
        )}

        {src ? (
          <>
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: 350,
                borderRadius: 4,
                overflow: 'hidden',
                border: '1px solid #ddd',
                background: '#f5f5f5',
              }}
            >
              <Cropper
                ref={cropperRef}
                src={src}
                style={{
                  width: '100%',
                  height: '100%',
                }}
                aspectRatio={1}
                viewMode={1}
                dragMode="move"
                autoCropArea={0.8}
                guides={false}
                background={false}
                responsive={true}
                restore={false}
                center={true}
                checkOrientation={false}
                cropBoxMovable={false}
                cropBoxResizable={false}
                toggleDragModeOnDblclick={false}
                zoomOnWheel={true}
                zoomOnTouch={true}
                highlight={false}
              />
            </div>

            <div
              style={{
                fontSize: 13,
                color: '#605e5c',
                textAlign: 'center',
              }}
            >
              Перетаскивайте изображение мышью.
              <br />
              Для изменения масштаба используйте колесо
              мыши.
            </div>
          </>
        ) : (
          <div
            style={{
              textAlign: 'center',
              color: '#999',
              padding: 20,
            }}
          >
            Нет изображения для обрезки
          </div>
        )}

        <Stack
          horizontal
          tokens={{ childrenGap: 10 }}
          style={{
            marginTop: 10,
          }}
        >
          <PrimaryButton
            text={
              isCropping
                ? 'Обработка...'
                : 'Сохранить'
            }
            onClick={handleSave}
            disabled={
              !src ||
              !!fileError ||
              isCropping
            }
          />

          <DefaultButton
            text="Отмена"
            onClick={onClose}
            disabled={isCropping}
          />
        </Stack>
      </Stack>
    </Modal>
  );
};